import React, { useEffect, useState } from "react";
import { X, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { classifyFeeKind } from "@/lib/fees";
import DateInput from "@/components/DateInput";

const CATEGORIES = [
  "Revenue", "Software", "Rent", "Utilities", "Meals", "Travel",
  "Supplies", "Marketing", "Insurance", "Contractors", "Education",
  "Equipment", "Taxes", "Debt Payment", "Other",
];

/**
 * Modal to edit an existing transaction. The form mirrors NewTransactionForm
 * but PATCHes /transactions/{id} and reports back with the updated record.
 */
export default function EditTransactionModal({ txn, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: txn.date,
    description: txn.description,
    // Store amount as absolute number in the field; sign is derived from type
    amount: String(Math.abs(txn.amount ?? 0)),
    category: txn.category || "Other",
    type: txn.type || "expense",
    is_tax_deductible: !!txn.is_tax_deductible,
    notes: txn.notes || "",
    bank_account_id: txn.bank_account_id || "",
  });
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bank-accounts");
        setAccounts(data.accounts || []);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const feeKind = form.type === "expense" ? classifyFeeKind(form.description) : null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amt = parseFloat(form.amount);
      if (isNaN(amt)) {
        toast.error("Amount must be a number");
        setSaving(false);
        return;
      }
      const signed = form.type === "expense" ? -Math.abs(amt) : Math.abs(amt);
      const payload = { ...form, amount: signed };
      // Send `null` explicitly to allow unassigning
      if (payload.bank_account_id === "") payload.bank_account_id = null;
      const { data } = await api.patch(`/transactions/${txn.id}`, payload);
      toast.success("Transaction updated");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm"
      data-testid="edit-transaction-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2 flex items-start justify-between">
          <div>
            <div className="overline mb-1">Edit transaction</div>
            <h3 className="font-heading text-xl font-medium">Update the details below</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-lighter hover:text-ink p-1"
            data-testid="edit-txn-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 pt-2 space-y-4" data-testid="edit-transaction-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="text-sm">
              <div className="font-medium mb-1.5">Date</div>
              <DateInput
                required
                value={form.date}
                onChange={(iso) => setForm({ ...form, date: iso })}
                data-testid="edit-txn-date"
              />
            </label>
            <label className="text-sm md:col-span-2">
              <div className="font-medium mb-1.5">Description</div>
              <input
                required
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="edit-txn-description"
                className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              />
              {feeKind && (
                <div className="mt-2 flex items-start gap-2 text-xs bg-ochre/10 border border-ochre/25 rounded-md px-3 py-2">
                  <AlertOctagon className="w-3.5 h-3.5 text-ochre mt-0.5 shrink-0" strokeWidth={1.75} />
                  <div>
                    <span className="font-medium text-ink">
                      {feeKind === "nsf" ? "Looks like an NSF / overdraft charge." : "Looks like a bank fee."}
                    </span>
                    <span className="text-ink-light ml-1">
                      Many of these are refundable — call your bank for a courtesy reversal.
                    </span>
                  </div>
                </div>
              )}
            </label>
            <label className="text-sm">
              <div className="font-medium mb-1.5">Type</div>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="edit-txn-type"
                className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              >
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </label>
            <label className="text-sm">
              <div className="font-medium mb-1.5">Amount</div>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="edit-txn-amount"
                className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none num"
              />
            </label>
            <label className="text-sm">
              <div className="font-medium mb-1.5">Category</div>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="edit-txn-category"
                className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          {accounts.length > 0 && (
            <label className="text-sm block">
              <div className="font-medium mb-1.5">Bank account <span className="text-ink-lighter font-normal">(optional)</span></div>
              <select
                value={form.bank_account_id}
                onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
                data-testid="edit-txn-bank-account"
                className="w-full bg-white border border-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
              >
                <option value="">— Unassigned —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.mask ? ` · ••${a.mask}` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_tax_deductible}
              onChange={(e) => setForm({ ...form, is_tax_deductible: e.target.checked })}
              data-testid="edit-txn-deductible"
              className="accent-forest"
            />
            <span>Tax deductible</span>
          </label>
          {txn.qbo_synced_at && (
            <div className="text-xs text-ochre bg-ochre/10 border border-ochre/25 rounded-md px-3 py-2">
              This transaction is already synced to QuickBooks. Edits will only apply inside BizCashflow Pros — QuickBooks will keep its own copy unless you delete it and push again.
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              data-testid="edit-txn-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-forest"
              data-testid="edit-txn-save"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
